import { formatCurrency } from '../../utils/formatCurrency';
import {
  DiscountedPrice,
  DiscountRow,
  OriginalPrice,
  PriceAmount,
  PriceLabel,
  Wrapper,
} from './PriceDisplay.styles';
import type {
  DiscountPriceDisplayProps,
  FromPriceDisplayProps,
  PriceDisplayProps,
} from './PriceDisplay.types';

export function PriceDisplay({
  amount,
  currencyCode,
  label,
  locale,
  noPennies,
  numberOfGuests,
  size = 'medium',
}: PriceDisplayProps) {
  const guests = numberOfGuests || 1;
  const formatted = formatCurrency(amount * guests, currencyCode, {
    locale,
    minimumFractionDigits: noPennies ? 0 : 2,
    maximumFractionDigits: noPennies ? 0 : 2,
  });

  if (label) {
    return (
      <Wrapper>
        <PriceLabel $size={size}>{label}</PriceLabel>
        <PriceAmount $size={size}>{formatted}</PriceAmount>
      </Wrapper>
    );
  }

  return <PriceAmount $size={size}>{formatted}</PriceAmount>;
}

export function DiscountPriceDisplay({
  price,
  discount,
  locale,
  noPennies,
  numberOfGuests,
  size = 'medium',
}: DiscountPriceDisplayProps) {
  const guests = numberOfGuests || 1;
  const discountPrice = discount?.discountedPrice;
  const hasDiscount = discountPrice && discountPrice.amount !== price.amount;

  if (!hasDiscount) {
    return (
      <PriceDisplay
        amount={price.amount}
        currencyCode={price.currencyCode}
        locale={locale}
        noPennies={noPennies}
        numberOfGuests={numberOfGuests}
        size={size}
      />
    );
  }

  const formattedOriginal = formatCurrency(price.amount * guests, price.currencyCode, {
    locale,
    minimumFractionDigits: noPennies ? 0 : 2,
    maximumFractionDigits: noPennies ? 0 : 2,
  });
  const formattedDiscount = formatCurrency(
    discountPrice.amount * guests,
    discountPrice.currencyCode,
    { locale, minimumFractionDigits: noPennies ? 0 : 2, maximumFractionDigits: noPennies ? 0 : 2 }
  );

  return (
    <DiscountRow>
      <DiscountedPrice $size={size}>{formattedDiscount}</DiscountedPrice>
      <OriginalPrice $size={size}>{formattedOriginal}</OriginalPrice>
    </DiscountRow>
  );
}

export function FromPriceDisplay({
  amount,
  currencyCode,
  label = 'From',
  discount,
  locale,
  noPennies,
  numberOfGuests,
  size = 'medium',
}: FromPriceDisplayProps) {
  return (
    <Wrapper>
      <PriceLabel $size={size}>{label}</PriceLabel>
      <DiscountPriceDisplay
        price={{ amount, currencyCode }}
        discount={discount}
        locale={locale}
        noPennies={noPennies}
        numberOfGuests={numberOfGuests}
        size={size}
      />
    </Wrapper>
  );
}
